import {
  getAdminV1AuthProviders,
  getAdminV1Auth2fa,
  getAdminV1AuthInvitationsToken,
  postAdminV1Auth2faDisable,
  postAdminV1Auth2faEnable,
  postAdminV1Auth2faRecoveryCodes,
  postAdminV1Auth2faSetup,
  postAdminV1Auth2faVerify,
  postAdminV1AuthGoogleCallback,
  postAdminV1AuthGoogleStart,
  postAdminV1AuthInvitationsAccept,
  postAdminV1AuthLogin,
  postAdminV1AuthMagicLink,
  postAdminV1AuthMagicLinkVerify,
  postAdminV1AuthPasswordForgot,
  postAdminV1AuthPasswordReset,
  postAdminV1AuthSwitchOrganization,
  type ForgotPasswordRequest,
  type GoogleCallbackRequest,
  type GoogleStart,
  type GoogleStartRequest,
  type SignInProviders,
  type InvitationPreview,
  type MagicLinkRequest,
  type MagicLinkVerifyRequest,
  type PostAdminV1AuthInvitationsAcceptBody,
  type ResetPasswordRequest,
  type StaffLoginRequest,
  type TokenResponse,
  type TwoFactorChallenge,
  type TwoFactorCodeRequest,
  type TwoFactorDisableRequest,
  type TwoFactorEnabled,
  type TwoFactorSetup,
  type TwoFactorStatus,
  type TwoFactorVerifyRequest,
} from '@nucleus-commerce/api-client/admin';

/**
 * Login, magic-link verify and the Google callback answer 200 with tokens or 202 with a
 * two-factor challenge.
 */
export type SignInResult = TokenResponse | TwoFactorChallenge;

/**
 * Thin wrapper over the generated client. Login, two-factor verify, magic-link verify, the
 * Google callback and accepting an invitation set the `nucleus_staff` HttpOnly cookie; the access token in the body
 * exists for API clients and the browser app never stores it. The public endpoints answer 401
 * for a bad credential, code or token, which must not be mistaken for an ended session, hence
 * `skipUnauthorizedHook`. The /auth/2fa management calls run inside a session and keep the hook.
 */
export const authApi = {
  login: (body: StaffLoginRequest): Promise<SignInResult> =>
    postAdminV1AuthLogin(body, { skipUnauthorizedHook: true }),
  verifyTwoFactor: (body: TwoFactorVerifyRequest): Promise<TokenResponse> =>
    postAdminV1Auth2faVerify(body, { skipUnauthorizedHook: true }),
  requestMagicLink: (body: MagicLinkRequest): Promise<void> =>
    postAdminV1AuthMagicLink(body, { skipUnauthorizedHook: true }),
  verifyMagicLink: (body: MagicLinkVerifyRequest): Promise<SignInResult> =>
    postAdminV1AuthMagicLinkVerify(body, { skipUnauthorizedHook: true }),
  invitationPreview: (token: string): Promise<InvitationPreview> =>
    getAdminV1AuthInvitationsToken({ token }, { skipUnauthorizedHook: true }),
  acceptInvitation: (body: PostAdminV1AuthInvitationsAcceptBody): Promise<TokenResponse> =>
    postAdminV1AuthInvitationsAccept(body, { skipUnauthorizedHook: true }),
  forgotPassword: (body: ForgotPasswordRequest): Promise<void> =>
    postAdminV1AuthPasswordForgot(body, { skipUnauthorizedHook: true }),
  resetPassword: (body: ResetPasswordRequest): Promise<void> =>
    postAdminV1AuthPasswordReset(body, { skipUnauthorizedHook: true }),
  providers: (): Promise<SignInProviders> =>
    getAdminV1AuthProviders({ skipUnauthorizedHook: true }),
  googleStart: (body: GoogleStartRequest): Promise<GoogleStart> =>
    postAdminV1AuthGoogleStart(body, { skipUnauthorizedHook: true }),
  googleCallback: (body: GoogleCallbackRequest): Promise<SignInResult> =>
    postAdminV1AuthGoogleCallback(body, { skipUnauthorizedHook: true }),
  /** Moves this sign-in to another organisation from /me `organizations`; 404 when not a member. */
  switchOrganization: (organizationId: string): Promise<TokenResponse> =>
    postAdminV1AuthSwitchOrganization({ organizationId }),
};

export const twoFactorApi = {
  status: (): Promise<TwoFactorStatus> => getAdminV1Auth2fa(),
  setup: (): Promise<TwoFactorSetup> => postAdminV1Auth2faSetup(),
  enable: (body: TwoFactorCodeRequest): Promise<TwoFactorEnabled> => postAdminV1Auth2faEnable(body),
  regenerateRecoveryCodes: (body: TwoFactorCodeRequest): Promise<TwoFactorEnabled> =>
    postAdminV1Auth2faRecoveryCodes(body),
  disable: (body: TwoFactorDisableRequest): Promise<void> => postAdminV1Auth2faDisable(body),
};
